import { computed, Injectable, signal } from '@angular/core';

import { AuthenticateResult } from '../models/auth.model';
import { SessionState, SessionUser } from '../models/session.model';

const SESSION_KEY = 'prizm.wms.mobile.session';
const REFRESH_TOKEN_KEY = 'prizm.wms.mobile.refresh_token';

@Injectable({ providedIn: 'root' })
export class SessionService {
  private readonly state = signal<SessionState>(this.readInitialState());

  readonly isAuthenticated = computed(() => this.state().isAuthenticated);
  readonly user = computed(() => this.state().user);
  readonly accessToken = computed(() => this.state().accessToken);

  setSession(result: AuthenticateResult): void {
    const expireAt = new Date(Date.now() + result.expireInSeconds * 1000).toISOString();

    const user: SessionUser = {
      id: result.userId,
      username: '',
      displayName: '',
    };

    this.setState({ isAuthenticated: true, accessToken: result.accessToken, expireAt, user });

    if (result.refreshToken) {
      globalThis.localStorage?.setItem(REFRESH_TOKEN_KEY, result.refreshToken);
    }
  }

  updateToken(accessToken: string, expireInSeconds: number): void {
    const expireAt = new Date(Date.now() + expireInSeconds * 1000).toISOString();
    const current = this.state();
    this.setState({ ...current, accessToken, expireAt });
  }

  updateDisplayName(displayName: string): void {
    const current = this.state();
    if (!current.user) return;
    this.setState({ ...current, user: { ...current.user, displayName } });
  }

  getRefreshToken(): string | null {
    return globalThis.localStorage?.getItem(REFRESH_TOKEN_KEY) ?? null;
  }

  logout(): void {
    this.setState({ isAuthenticated: false, accessToken: null, expireAt: null, user: null });
    globalThis.localStorage?.removeItem(REFRESH_TOKEN_KEY);
  }

  private readInitialState(): SessionState {
    const stored = globalThis.sessionStorage?.getItem(SESSION_KEY);
    if (!stored) return this.emptyState();

    try {
      return JSON.parse(stored) as SessionState;
    } catch {
      return this.emptyState();
    }
  }

  private setState(next: SessionState): void {
    this.state.set(next);
    globalThis.sessionStorage?.setItem(SESSION_KEY, JSON.stringify(next));
  }

  private emptyState(): SessionState {
    return { isAuthenticated: false, accessToken: null, expireAt: null, user: null };
  }
}
